import { useEffect, useMemo, useState } from 'react'
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline'

import {
  Badge,
  Button,
  Input,
  Panel,
  PanelBody,
  PanelDescription,
  PanelHeader,
  PanelTitle,
  Select,
  SelectOption,
} from '../../components/ui'
import { cn } from '../../shared/lib/cn'
import {
  useClearCloudAccrualDataSource,
  useCloudAccrualAvailableConnectors,
  useCloudAccrualDataSourceConfig,
  useCloudAccrualHealth,
  useSetCloudAccrualDataSource,
  useTestCloudAccrualConnection,
} from './api'
import type { CloudAccrualConfig, CloudAccrualHealth } from './types'

const DEFAULT_SCHEMA = 'cloud_accruals'

export default function ConfigPage() {
  const { data: config, isLoading: configLoading } = useCloudAccrualDataSourceConfig()
  const { data: health, isLoading: healthLoading } = useCloudAccrualHealth()
  const { data: connectors = [], isLoading: connectorsLoading } = useCloudAccrualAvailableConnectors()
  const setDataSource = useSetCloudAccrualDataSource()
  const clearDataSource = useClearCloudAccrualDataSource()
  const testConnection = useTestCloudAccrualConnection()

  const [connectorId, setConnectorId] = useState('')
  const [schema, setSchema] = useState(DEFAULT_SCHEMA)
  const [bootstrapped, setBootstrapped] = useState(false)

  useEffect(() => {
    if (!config || bootstrapped) return
    setConnectorId(config.connector_id ?? '')
    setSchema(config.redshift_schema || DEFAULT_SCHEMA)
    setBootstrapped(true)
  }, [bootstrapped, config])

  const activeConnector = useMemo(
    () => connectors.find(connector => connector.connector_id === connectorId),
    [connectorId, connectors],
  )

  const isLoading = configLoading || healthLoading || connectorsLoading
  const trimmedSchema = schema.trim() || DEFAULT_SCHEMA
  const canSubmit = Boolean(connectorId && trimmedSchema)

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        Loading configuration...
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-[1180px] space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-charcoal dark:text-gray-100">
            Configuration
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Bind Cloud Accruals to the Redshift schema used for provider billing evidence.
          </p>
        </div>
        <ConnectionBadges config={config} health={health} />
      </div>

      <Panel>
        <PanelHeader>
          <PanelTitle>Data Source</PanelTitle>
          <PanelDescription>
            Select the saved Redshift connector and table schema for this applet.
          </PanelDescription>
        </PanelHeader>
        <PanelBody className="space-y-5">
          <div className="grid grid-cols-[minmax(0,1.15fr)_minmax(260px,0.85fr)] gap-4 max-lg:grid-cols-1">
            <div className="space-y-2">
              <label className="text-sm font-medium text-charcoal dark:text-gray-100">
                Redshift Connector
              </label>
              <Select
                value={connectorId}
                onValueChange={setConnectorId}
                placeholder={connectors.length ? 'Select a connector' : 'No Redshift connectors available'}
                disabled={!connectors.length}
                icon={<LinkIcon />}
              >
                {connectors.map(connector => (
                  <SelectOption key={connector.connector_id} value={connector.connector_id}>
                    {connector.connector_name}
                  </SelectOption>
                ))}
              </Select>
              {config?.connector_name && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Current: {config.connector_name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-charcoal dark:text-gray-100">
                Redshift Schema
              </label>
              <Input
                value={schema}
                onChange={event => setSchema(event.target.value)}
                placeholder={DEFAULT_SCHEMA}
              />
              {config?.redshift_schema && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Current: {config.redshift_schema}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="brand"
              onClick={() =>
                setDataSource.mutate({
                  connector_id: connectorId,
                  redshift_schema: trimmedSchema,
                })
              }
              disabled={!canSubmit || setDataSource.isPending}
            >
              {setDataSource.isPending ? <ArrowPathIcon className="animate-spin" /> : <CheckCircleIcon />}
              {setDataSource.isPending ? 'Saving' : 'Save Configuration'}
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                testConnection.mutate({
                  connector_id: connectorId,
                  redshift_schema: trimmedSchema,
                })
              }
              disabled={!canSubmit || testConnection.isPending}
            >
              {testConnection.isPending ? <ArrowPathIcon className="animate-spin" /> : <ServerStackIcon />}
              {testConnection.isPending ? 'Testing' : 'Test Connection'}
            </Button>
            {config?.mode === 'saved' && (
              <Button
                variant="destructive"
                onClick={() => {
                  if (window.confirm('Clear the saved Cloud Accruals Redshift connector?')) {
                    clearDataSource.mutate()
                  }
                }}
                disabled={clearDataSource.isPending}
              >
                {clearDataSource.isPending ? 'Clearing' : 'Clear Saved Connector'}
              </Button>
            )}
          </div>

          <MutationStatus
            saved={setDataSource.isSuccess}
            saveError={setDataSource.error}
            cleared={clearDataSource.isSuccess}
            clearError={clearDataSource.error}
            tested={testConnection.data}
            testError={testConnection.error}
          />

          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={activeConnector?.is_healthy ? 'success' : 'secondary'}>
                {activeConnector?.is_healthy ? 'Connector healthy' : 'Connector not selected'}
              </Badge>
              <Badge variant={health?.langfuse_enabled ? 'success' : 'warning'}>
                Langfuse {health?.langfuse_enabled ? 'enabled' : 'inactive'}
              </Badge>
            </div>
            <p className="mt-2 text-xs leading-5 text-gray-500 dark:text-gray-400">
              The applet expects seeded tables in schema <span className="font-mono">{trimmedSchema}</span>: cloud_accrual_runs, cloud_accrual_categories, cloud_accrual_usage_items, cloud_accrual_commitments, cloud_accrual_exports, cloud_accrual_source_files, and cloud_accrual_reports.
            </p>
          </div>
        </PanelBody>
      </Panel>
    </div>
  )
}

function ConnectionBadges({
  config,
  health,
}: {
  config?: CloudAccrualConfig
  health?: CloudAccrualHealth
}) {
  const connected = Boolean(config?.is_connected || health?.redshift_connected)
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant={connected ? 'success' : 'danger'}>
        Redshift {connected ? 'connected' : 'not connected'}
      </Badge>
      <Badge variant="secondary">{config?.mode ?? health?.mode ?? 'unconfigured'}</Badge>
      {config?.redshift_schema && <Badge variant="secondary">{config.redshift_schema}</Badge>}
    </div>
  )
}

function MutationStatus({
  saved,
  saveError,
  cleared,
  clearError,
  tested,
  testError,
}: {
  saved: boolean
  saveError: Error | null
  cleared: boolean
  clearError: Error | null
  tested?: CloudAccrualHealth
  testError: Error | null
}) {
  if (!saved && !saveError && !cleared && !clearError && !tested && !testError) return null

  const isError = Boolean(saveError || clearError || testError || (tested && !tested.redshift_connected))
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg border px-4 py-3 text-sm',
        isError
          ? 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/40 dark:bg-rose-950/20 dark:text-rose-300'
          : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-950/20 dark:text-emerald-300',
      )}
    >
      {isError ? <ExclamationTriangleIcon className="h-5 w-5" /> : <CheckCircleIcon className="h-5 w-5" />}
      <div>
        {saveError && <p>{saveError.message}</p>}
        {clearError && <p>{clearError.message}</p>}
        {testError && <p>{testError.message}</p>}
        {tested && <p>{tested.message || (tested.redshift_connected ? 'Connection test passed.' : 'Connection test failed.')}</p>}
        {saved && <p>Cloud Accruals data source saved.</p>}
        {cleared && <p>Saved Cloud Accruals data source cleared.</p>}
      </div>
    </div>
  )
}
